import { useSidebarV2 } from '#pplx/frontend/apps/ask/hooks/sidebar-graphql-v2/context.js';
import { getSpacesLandingV2ScopedQueryKey } from '#pplx/frontend/apps/ask/queries/query-keys/spacesKeys.js';
import { listSpacesLandingV2 } from '#pplx/frontend/apps/ask/queries/spaces-queries/listSpacesLandingV2.js';
import { EMPTY_ARRAY } from '#pplx/frontend/common/defaults.js';
import { useQuery } from '#pplx/frontend/react-query/useQuery.js';
import { keepPreviousData } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
const PROJECT_PREVIEW_COUNT = 5;
const PROJECT_FETCH_LIMIT = 10;
const PROJECT_SECTIONS = 'pinned,main';
// Anchors arrive unique, newest first, with UUID routes. Canonical rows replace their other
// fields; server pinned-section membership remains the source of truth for pinned filtering.
export function resolveVisibleSidebarProjects({ canonicalProjects, pinnedProjectUuids, anchoredProjects, isExpanded, reserveCreateSlot, showPinnedOnly, }) {
    const visibleLimit = isExpanded ? PROJECT_FETCH_LIMIT : PROJECT_PREVIEW_COUNT;
    const pinnedProjects = canonicalProjects.filter((project) => pinnedProjectUuids.has(project.uuid));
    const canonicalByUuid = new Map(canonicalProjects.map((project) => [project.uuid, project]));
    const anchoredProjectUuids = new Set(anchoredProjects.map((project) => project.uuid));
    const orderedProjects = [
        ...anchoredProjects
            .filter((project) => !showPinnedOnly || pinnedProjectUuids.has(project.uuid))
            .map((project) => ({
            ...(canonicalByUuid.get(project.uuid) ?? project),
            slug: project.slug,
        })),
        ...(showPinnedOnly ? pinnedProjects : canonicalProjects).filter((project) => !anchoredProjectUuids.has(project.uuid)),
    ];
    return {
        projects: orderedProjects.slice(0, Math.max(visibleLimit - Number(reserveCreateSlot), 0)),
        canToggleExpanded: orderedProjects.length + Number(reserveCreateSlot) > PROJECT_PREVIEW_COUNT,
    };
}
export function useSidebarProjects({ enabled, showPinnedOnly, anchoredProjects = EMPTY_ARRAY, reserveCreateSlot = false, }) {
    const sidebarV2 = useSidebarV2();
    const [isExpanded, setIsExpanded] = useState(false);
    const isGraphQL = sidebarV2 != null;
    useEffect(() => {
        if (isGraphQL)
            setIsExpanded(false);
    }, [isGraphQL, showPinnedOnly]);
    const { data, isLoading, isError, status, fetchStatus, refetch } = useQuery({
        queryKey: getSpacesLandingV2ScopedQueryKey({
            limit: PROJECT_FETCH_LIMIT,
            sections: PROJECT_SECTIONS,
            ordered: true,
        }),
        enabled: enabled && sidebarV2 == null,
        placeholderData: keepPreviousData,
        queryFn: () => listSpacesLandingV2({
            params: {
                limit: PROJECT_FETCH_LIMIT,
                sections: PROJECT_SECTIONS,
                ordered: true,
            },
            reason: 'sidebar-projects',
        }),
    });
    const { allProjects, pinnedProjectUuids } = useMemo(() => {
        if (sidebarV2)
            return {
                allProjects: sidebarV2.projects,
                pinnedProjectUuids: new Set(sidebarV2.projects.filter((project) => project.is_pinned).map((project) => project.uuid)),
            };
        const pinnedProjects = data?.sections?.pinned?.items ?? EMPTY_ARRAY;
        const mainProjects = data?.sections?.main?.items ?? EMPTY_ARRAY;
        const seenProjectUuids = new Set();
        const projects = [...pinnedProjects, ...mainProjects].filter((project) => {
            if (seenProjectUuids.has(project.uuid))
                return false;
            seenProjectUuids.add(project.uuid);
            return true;
        });
        return {
            allProjects: projects.map((project) => ({ ...project, collection: project })),
            pinnedProjectUuids: new Set(pinnedProjects.map((project) => project.uuid)),
        };
    }, [sidebarV2, data?.sections?.main?.items, data?.sections?.pinned?.items]);
    const { projects, canToggleExpanded } = useMemo(() => resolveVisibleSidebarProjects({
        canonicalProjects: allProjects,
        pinnedProjectUuids,
        anchoredProjects,
        isExpanded,
        reserveCreateSlot,
        showPinnedOnly,
    }), [
        allProjects,
        anchoredProjects,
        isExpanded,
        pinnedProjectUuids,
        reserveCreateSlot,
        showPinnedOnly,
    ]);
    const toggleExpanded = useCallback(() => {
        if (!isExpanded && sidebarV2?.projectsHasNextPage)
            sidebarV2.loadMoreProjects();
        setIsExpanded((current) => !current);
    }, [isExpanded, sidebarV2]);
    const retry = useCallback(() => {
        if (enabled)
            void refetch();
    }, [enabled, refetch]);
    const hasData = data !== undefined;
    const graphqlResult = useMemo(() => {
        if (!sidebarV2)
            return null;
        const common = {
            projects,
            isLoading: sidebarV2.projectsLoading,
            isError: sidebarV2.projectsError,
            fetchStatus: sidebarV2.projectsLoading ? 'fetching' : 'idle',
            isExpanded,
            canToggleExpanded: canToggleExpanded || sidebarV2.projectsHasNextPage,
            toggleExpanded,
            retry: sidebarV2.retry,
        };
        if (common.isLoading)
            return { ...common, status: 'pending', hasData: false };
        if (common.isError)
            return { ...common, status: 'error', hasData: sidebarV2.projectsHasData };
        return { ...common, status: 'success', hasData: true };
    }, [sidebarV2, projects, isExpanded, canToggleExpanded, toggleExpanded]);
    return useMemo(() => {
        if (graphqlResult)
            return graphqlResult;
        const result = {
            projects,
            isLoading,
            isError,
            fetchStatus,
            isExpanded,
            canToggleExpanded,
            toggleExpanded,
            retry,
        };
        switch (status) {
            case 'pending':
                return { ...result, status, hasData: false };
            case 'success':
                return { ...result, status, hasData: true };
            case 'error':
                return { ...result, status, hasData };
        }
    }, [
        graphqlResult,
        projects,
        isLoading,
        isError,
        status,
        fetchStatus,
        hasData,
        isExpanded,
        canToggleExpanded,
        toggleExpanded,
        retry,
    ]);
}
//# sourceMappingURL=useSidebarProjects.js.map